import { useEffect } from "react";

/**
 * SEO — per-route meta tag helper.
 *
 * React 19 natively hoists `<title>`, `<meta>`, and `<link>` tags rendered
 * anywhere in the component tree into `<head>`. Last write wins, so an
 * indexable page can simply render <SEO ... /> to override the sitewide
 * defaults from public/index.html.
 *
 * Usage:
 *   import { SEO } from "../lib/SEO";
 *   <SEO title="Founding Donors — My Rich Aunt"
 *        description="The people who believed first..."
 *        path="/founding" />
 *
 * Do NOT use this on authenticated / private / demo routes — those use
 * `useNoIndex()` instead, which emits <meta name="robots" content="noindex">.
 */

const SITE_URL = "https://myrichaunt.org";

/** Drop the static `data-seo-default` fallbacks from index.html once a page renders its own tags. */
const removeStaticDefaults = () => {
  if (typeof document === "undefined") return;
  document.querySelectorAll("head [data-seo-default]").forEach((el) => el.remove());
};

export const SEO = ({
  title,
  description,
  path = "/",
  image = "/og-image.jpg",
  imageAlt,
}) => {
  useEffect(removeStaticDefaults, []);
  const url = `${SITE_URL}${path}`;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const altText = imageAlt || title;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph — how this URL shows up when shared on FB/LinkedIn/iMessage */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="My Rich Aunt" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:alt" content={altText} />

      {/* Twitter — same info, twitter-flavored */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
    </>
  );
};
